'use client'

import { useMemo, useState, useSyncExternalStore } from 'react'
import Image from 'next/image'
import MugPhoto, { measureMugFontSize } from '@/components/marketing/MugPhoto'

const C = {
  ink: '#0A0A0A',
  muted: '#52525B',
  mutedSoft: '#71717a',
  rule: 'rgba(20,20,20,0.08)',
  gray700: '#374151',
  accent: '#E55934',
  accentSoft: 'rgba(229, 89, 52, 0.10)',
  warm: '#E55934',
}

const MAX_NAME_LENGTH = 10

/**
 * Hero visual — product mug + editable Name field.
 * The mug's curved headline ("HAPPY 30TH") and the body text both update
 * live as the visitor types into the field, so the value-prop "live preview"
 * is something you can actually feel by interacting.
 */
export default function HeroEditor() {
  const [name, setName] = useState('Emma')
  const displayName = name.trim() || 'Emma'

  return (
    <div style={{ position: 'relative', maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
      {/* Helter-skelter spiral behind the product card */}
      <div aria-hidden className="hs-swirl" style={{ inset: '-6% -4%', zIndex: 0 }} />
      {/* Soft gradient halo behind the mug */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '-24px -32px',
          background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(229,89,52,0.18), transparent 70%)',
          filter: 'blur(20px)',
          zIndex: 0,
        }}
      />

      {/* YES! disc, stuck on at an angle like a fairground sticker */}
      <Image
        src="/yes-mark.png"
        alt=""
        aria-hidden
        width={1160}
        height={1150}
        className="hs-sticker"
        style={{ width: 96, height: 'auto', right: -34, bottom: -30 }}
      />

      {/* Main product card */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          background: '#ffffff',
          borderRadius: 24,
          border: `1px solid ${C.rule}`,
          padding: '32px 32px 28px',
          boxShadow: '0 30px 80px -30px rgba(20,20,20,0.20), 0 1px 0 rgba(20,20,20,0.04) inset',
          transform: 'rotate(-1.1deg)',
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="saas-pulse" />
            <span
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 10,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: C.muted,
              }}
            >
              Live preview
            </span>
          </div>
          <span
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 10,
              color: C.mutedSoft,
              letterSpacing: '0.14em',
            }}
          >
            Birthday Mug · 11oz
          </span>
        </div>

        <div
          style={{
            background: 'linear-gradient(180deg, #FAFAFA 0%, #F4F4F5 100%)',
            borderRadius: 14,
            padding: '20px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
          }}
        >
          <HeroMug name={displayName} />
        </div>

        {/* Field row — real, editable */}
        <div style={{ marginTop: 18 }}>
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="hero-name"
              style={{ fontSize: 12, fontWeight: 500, color: C.gray700 }}
            >
              Name
            </label>
            <span
              style={{
                fontSize: 10,
                color: C.accent,
                fontFamily: "'IBM Plex Sans', sans-serif",
                letterSpacing: '0.12em',
                fontWeight: 600,
              }}
            >
              ● TYPING
            </span>
          </div>
          <input
            id="hero-name"
            type="text"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e) => setName(e.target.value)}
            placeholder="Type a name"
            autoComplete="off"
            spellCheck={false}
            style={{
              width: '100%',
              border: `1.5px solid ${C.accent}`,
              padding: '10px 12px',
              fontSize: 14,
              fontWeight: 500,
              color: C.ink,
              borderRadius: 10,
              background: '#fff',
              boxShadow: `0 0 0 4px ${C.accentSoft}`,
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Floating annotation: curved text */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          right: -8, top: 86, zIndex: 2,
          background: '#0A0A0A',
          color: '#fff',
          padding: '10px 14px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 500,
          boxShadow: '0 16px 30px -10px rgba(20,20,20,0.4)',
          maxWidth: 200,
          transform: 'rotate(2deg)',
        }}
        className="hidden lg:block"
      >
        <div style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: '#f7c5b3', marginBottom: 4, fontWeight: 600,
        }}>Layer · Curved</div>
        Arch up · radius 42
        <span
          aria-hidden
          style={{
            position: 'absolute', left: -7, top: 18,
            width: 0, height: 0,
            borderTop: '7px solid transparent',
            borderBottom: '7px solid transparent',
            borderRight: '7px solid #0A0A0A',
          }}
        />
      </div>

      {/* Floating annotation: artwork ready */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: -30, bottom: 122, zIndex: 2,
          background: '#fff',
          border: `1px solid ${C.rule}`,
          padding: '10px 14px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 500,
          boxShadow: '0 16px 30px -10px rgba(20,20,20,0.18)',
          maxWidth: 180,
          transform: 'rotate(-3deg)',
        }}
        className="hidden lg:block"
      >
        <div style={{
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: '#15803d', marginBottom: 4, fontWeight: 600,
        }}>● Artwork ready</div>
        <span style={{ color: C.ink }}>Rendered in 2.1 s</span>
      </div>
    </div>
  )
}

/**
 * The hero mug photo, with the name sized to fill the print area.
 *
 * The measurement runs against real DM Sans metrics, so the size settles once
 * the webfont has loaded rather than being measured against the fallback.
 */
// Webfont readiness as an external store: notifies once document.fonts.ready
// resolves so the measurement below re-runs against real DM Sans metrics.
function subscribeFontsReady(onChange: () => void) {
  let live = true
  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(() => { if (live) onChange() }).catch(() => {})
  }
  return () => { live = false }
}
const getFontsReadySnapshot = () => document.fonts?.status === 'loaded'
const getFontsReadyServerSnapshot = () => false

function HeroMug({ name }: { name: string }) {
  const fontsReady = useSyncExternalStore(subscribeFontsReady, getFontsReadySnapshot, getFontsReadyServerSnapshot)
  // Derived during render: re-measures when the name changes or fonts finish loading.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fontsReady is an intentional re-measure trigger
  const fontSize = useMemo(() => measureMugFontSize(name), [name, fontsReady])

  return (
    <MugPhoto
      uid="hero"
      name={name}
      nameFontSize={fontSize}
      width={280}
      priority
    />
  )
}
